import React, { useState, useEffect, useMemo } from "react";
import "./CountdownElectoral.css";

/**
 * Componente independiente de Cuenta Regresiva para la Jornada Electoral 2027.
 * Con fondo morado luminoso y clase exclusiva .titjornada (100% blanco puro).
 */
const CountdownElectoral = ({
  targetDate = "2027-06-06T07:00:00",
  subtitle = "Domingo, 6 de junio de 2027",
}) => {
  const target = useMemo(() => new Date(targetDate), [targetDate]);

  const calculateTimeLeft = useMemo(() => {
    return () => {
      const now = new Date();
      const diff = target.getTime() - now.getTime();

      if (diff <= 0) {
        return {
          isFinished: true,
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
        };
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / 1000 / 60) % 60);
      const seconds = Math.floor((diff / 1000) % 60);

      return {
        isFinished: false,
        days,
        hours,
        minutes,
        seconds,
      };
    };
  }, [target]);

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(interval);
  }, [calculateTimeLeft]);

  const formatNumber = (num, minDigits = 2) => {
    return String(num).padStart(minDigits, "0");
  };

  return (
    <div className="w-100 mb-4 countdown-electoral">
      <section className="countdown-electoral__container" aria-label="Cuenta regresiva electoral 2027">
        {/* Fondo dinámico SVG: ondas fluidas, degradados y partículas */}
        <svg
          className="countdown-electoral__bg-svg"
          viewBox="0 0 1200 320"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="ce-wave-grad-1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.15" />
              <stop offset="40%" stopColor="#ff7ad0" stopOpacity="0.45" />
              <stop offset="70%" stopColor="#ffaae5" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0.2" />
            </linearGradient>
            <linearGradient id="ce-wave-grad-2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.08" />
              <stop offset="50%" stopColor="#ffffff" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#ff85d5" stopOpacity="0.18" />
            </linearGradient>
            <radialGradient id="ce-glow-corner-right" cx="100%" cy="0%" r="70%">
              <stop offset="0%" stopColor="#e5389c" stopOpacity="0.32" />
              <stop offset="60%" stopColor="#a02888" stopOpacity="0.1" />
              <stop offset="100%" stopColor="#601b63" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="ce-glow-corner-left" cx="0%" cy="100%" r="70%">
              <stop offset="0%" stopColor="#7d207a" stopOpacity="0.35" />
              <stop offset="70%" stopColor="#601b63" stopOpacity="0" />
            </radialGradient>
            <filter id="ce-glow-filter" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="2.5" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          </defs>

          {/* Formas curvas suaves en esquinas */}
          <rect x="0" y="0" width="1200" height="320" fill="url(#ce-glow-corner-right)" />
          <rect x="0" y="0" width="1200" height="320" fill="url(#ce-glow-corner-left)" />

          {/* Ondas luminosas continuas con corriente de luz animada */}
          <path
            className="countdown-electoral__flow-line countdown-electoral__flow-line--1"
            d="M-100 240 C200 320, 380 120, 680 200 C980 280, 1100 100, 1300 80"
            stroke="url(#ce-wave-grad-1)"
            strokeWidth="2.2"
            strokeLinecap="round"
            filter="url(#ce-glow-filter)"
          />
          <path
            className="countdown-electoral__flow-line countdown-electoral__flow-line--2"
            d="M-50 160 C250 80, 480 260, 780 140 C1020 40, 1180 180, 1320 160"
            stroke="url(#ce-wave-grad-2)"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
          <path
            className="countdown-electoral__flow-line countdown-electoral__flow-line--3"
            d="M-80 60 C220 10, 440 140, 720 70 C960 10, 1120 110, 1280 90"
            stroke="rgba(255, 180, 230, 0.35)"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
          <path
            className="countdown-electoral__flow-line countdown-electoral__flow-line--pulse"
            d="M-100 240 C200 320, 380 120, 680 200 C980 280, 1100 100, 1300 80"
            stroke="rgba(255, 255, 255, 0.75)"
            strokeWidth="2.4"
            strokeLinecap="round"
            filter="url(#ce-glow-filter)"
          />

          {/* Partículas y destellos sutiles flotantes */}
          <circle className="countdown-electoral__particle countdown-electoral__particle--1" cx="220" cy="90" r="1.5" fill="#ffffff" />
          <circle className="countdown-electoral__particle countdown-electoral__particle--2" cx="340" cy="220" r="2" fill="#ff9ee0" />
          <circle className="countdown-electoral__particle countdown-electoral__particle--3" cx="540" cy="60" r="1.5" fill="#ffffff" />
          <circle className="countdown-electoral__particle countdown-electoral__particle--4" cx="720" cy="250" r="2.5" fill="#ff9ee0" />
          <circle className="countdown-electoral__particle countdown-electoral__particle--5" cx="920" cy="75" r="2" fill="#ffffff" />
          <circle className="countdown-electoral__particle countdown-electoral__particle--6" cx="1040" cy="190" r="1.5" fill="#ff9ee0" />
          <circle className="countdown-electoral__particle countdown-electoral__particle--7" cx="1120" cy="110" r="2" fill="#ffffff" />
          <circle className="countdown-electoral__particle countdown-electoral__particle--8" cx="150" cy="270" r="1.5" fill="#ffffff" />
        </svg>

        {/* Contenido: Estado Final vs Estado Activo */}
        {timeLeft.isFinished ? (
          <div className="countdown-final">
            {/* Badge PROCESO ELECTORAL 2027 */}
            <div className="countdown-electoral__badge-wrapper">
              <div className="countdown-electoral__badge-line countdown-electoral__badge-line--left" aria-hidden="true"></div>
              <div className="countdown-electoral__badge-spark countdown-electoral__badge-spark--left" aria-hidden="true">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" />
                </svg>
              </div>
              <span className="countdown-electoral__badge">PROCESO ELECTORAL 2027</span>
              <div className="countdown-electoral__badge-spark countdown-electoral__badge-spark--right" aria-hidden="true">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" />
                </svg>
              </div>
              <div className="countdown-electoral__badge-line countdown-electoral__badge-line--right" aria-hidden="true"></div>
            </div>

            {/* Icono Grande de Casilla / Urna Electoral */}
            <div className="countdown-final-icon" aria-hidden="true">
              <svg
                viewBox="0 0 100 100"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="countdown-final-icon-svg"
              >
                {/* Resplandor suave de fondo */}
                <circle cx="50" cy="52" r="44" fill="url(#ce-final-glow)" opacity="0.45" />

                {/* Boleta Electoral ingresando en la ranura */}
                <g className="countdown-final-ballot">
                  <rect
                    x="36"
                    y="10"
                    width="28"
                    height="38"
                    rx="3.5"
                    fill="#ffffff"
                    stroke="rgba(255, 255, 255, 0.95)"
                    strokeWidth="1.8"
                  />
                  <line x1="42" y1="18" x2="58" y2="18" stroke="#c62f92" strokeWidth="2.2" strokeLinecap="round" />
                  <line x1="42" y1="24" x2="54" y2="24" stroke="rgba(160, 40, 136, 0.7)" strokeWidth="1.8" strokeLinecap="round" />
                  <line x1="42" y1="30" x2="56" y2="30" stroke="rgba(160, 40, 136, 0.7)" strokeWidth="1.8" strokeLinecap="round" />
                  <circle cx="53" cy="38" r="5" fill="#e52892" />
                  <path
                    d="M51 38L52.5 39.5L55.5 36.5"
                    stroke="#ffffff"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>

                {/* Ranura receptora */}
                <rect x="29" y="42" width="42" height="7" rx="3.5" fill="#38093d" />
                <rect x="33" y="44.5" width="34" height="2.5" rx="1.2" fill="#1b0222" />

                {/* Cuerpo de la Urna Electoral */}
                <path
                  d="M18 45L24 88C24.4 90.5 26.5 92 29 92H71C73.5 92 75.6 90.5 76 88L82 45H18Z"
                  fill="url(#ce-final-urna-body)"
                  stroke="rgba(255, 255, 255, 0.55)"
                  strokeWidth="2.2"
                />

                {/* Tapa superior de la Urna */}
                <rect
                  x="15"
                  y="40"
                  width="70"
                  height="10"
                  rx="5"
                  fill="url(#ce-final-urna-top)"
                  stroke="rgba(255, 255, 255, 0.7)"
                  strokeWidth="1.8"
                />

                {/* Ventana frontal translúcida con silueta de boletas */}
                <rect
                  x="30"
                  y="56"
                  width="40"
                  height="26"
                  rx="4"
                  fill="rgba(255, 255, 255, 0.18)"
                  stroke="rgba(255, 255, 255, 0.45)"
                  strokeWidth="1.2"
                />
                <path
                  d="M36 72L50 64L64 72"
                  stroke="rgba(255, 255, 255, 0.6)"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />

                {/* Emblema central de casilla / voto */}
                <circle cx="50" cy="69" r="8" fill="#e52892" stroke="rgba(255, 255, 255, 0.8)" strokeWidth="1.5" />
                <path
                  d="M46.5 69L49 71.5L54 66.5"
                  stroke="#ffffff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />

                <defs>
                  <linearGradient id="ce-final-urna-body" x1="18" y1="45" x2="82" y2="92" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#73196c" stopOpacity="0.9" />
                    <stop offset="50%" stopColor="#9b2482" stopOpacity="0.85" />
                    <stop offset="100%" stopColor="#c42d8e" stopOpacity="0.9" />
                  </linearGradient>
                  <linearGradient id="ce-final-urna-top" x1="15" y1="40" x2="85" y2="50" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#d61f85" />
                    <stop offset="100%" stopColor="#981c7e" />
                  </linearGradient>
                  <radialGradient id="ce-final-glow" cx="50" cy="52" r="44" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#ff7ad0" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#c62f92" stopOpacity="0" />
                  </radialGradient>
                </defs>
              </svg>
            </div>

            {/* Mensaje Principal */}
            <h2 className="countdown-final-title">¡Hoy es el día!</h2>

            {/* Subtítulo Motivador */}
            <p className="countdown-final-message">Tu participación hace la diferencia.</p>

            {/* Llamado a la Acción Destacado */}
            <div className="countdown-final-action">
              <span>¡Sal a votar y haz escuchar tu voz!</span>
            </div>
          </div>
        ) : (
          <>
            {/* Encabezado Estado Activo */}
            <header className="countdown-electoral__header">
              {/* Badge con líneas horizontales y destellos laterales ✦ */}
              <div className="countdown-electoral__badge-wrapper">
                <div className="countdown-electoral__badge-line countdown-electoral__badge-line--left" aria-hidden="true"></div>
                <div className="countdown-electoral__badge-spark countdown-electoral__badge-spark--left" aria-hidden="true">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" />
                  </svg>
                </div>
                <span className="countdown-electoral__badge">PROCESO ELECTORAL 2027</span>
                <div className="countdown-electoral__badge-spark countdown-electoral__badge-spark--right" aria-hidden="true">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" />
                  </svg>
                </div>
                <div className="countdown-electoral__badge-line countdown-electoral__badge-line--right" aria-hidden="true"></div>
              </div>

              {/* TÍTULO: Clase exclusiva .titjornada - Blanco sólido sin contornos */}
              <h2 className="titjornada">
                Cuenta Regresiva para la <span className="titjornada-bold">Jornada Electoral</span>
              </h2>

              {/* Fecha */}
              <div className="countdown-electoral__date">
                <span className="countdown-electoral__date-icon" aria-hidden="true">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </span>
                <span>{subtitle}</span>
              </div>
            </header>

            {/* Tres Cards Horizontales - Tamaño y distribución intactos */}
            <div className="countdown-electoral__grid">
              {/* CARD 1: DÍAS */}
              <div className="countdown-electoral__card">
                <div className="countdown-electoral__card-inner">
                  <div className="countdown-electoral__icon-circle" aria-hidden="true">
                    <svg
                      className="countdown-electoral__svg-calendar"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                      <line x1="8" y1="14" x2="8" y2="14.01"></line>
                      <line x1="12" y1="14" x2="12" y2="14.01"></line>
                      <line x1="16" y1="14" x2="16" y2="14.01"></line>
                      <line x1="8" y1="18" x2="8" y2="18.01"></line>
                      <line x1="12" y1="18" x2="12" y2="18.01"></line>
                      <line x1="16" y1="18" x2="16" y2="18.01"></line>
                    </svg>
                  </div>
                  <div className="countdown-electoral__card-info">
                    <span className="countdown-electoral__number">
                      {timeLeft.days < 100 ? formatNumber(timeLeft.days) : timeLeft.days}
                    </span>
                    <span className="countdown-electoral__label">DÍAS</span>
                  </div>
                </div>
                <div className="countdown-electoral__card-line" aria-hidden="true"></div>
              </div>

              {/* CARD 2: HORAS */}
              <div className="countdown-electoral__card">
                <div className="countdown-electoral__card-inner">
                  <div className="countdown-electoral__icon-circle" aria-hidden="true">
                    <svg
                      className="countdown-electoral__svg-clock"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" className="countdown-electoral__clock-rim"></circle>
                      <line x1="12" y1="12" x2="12" y2="7.5" className="countdown-electoral__clock-hour"></line>
                      <line x1="12" y1="12" x2="15.5" y2="12" className="countdown-electoral__clock-minute"></line>
                      <circle cx="12" cy="12" r="1" fill="currentColor"></circle>
                    </svg>
                  </div>
                  <div className="countdown-electoral__card-info">
                    <span className="countdown-electoral__number">{formatNumber(timeLeft.hours)}</span>
                    <span className="countdown-electoral__label">HORAS</span>
                  </div>
                </div>
                <div className="countdown-electoral__card-line" aria-hidden="true"></div>
              </div>

              {/* CARD 3: MINUTOS */}
              <div className="countdown-electoral__card">
                <div className="countdown-electoral__card-inner">
                  <div className="countdown-electoral__icon-circle" aria-hidden="true">
                    <svg
                      className="countdown-electoral__svg-stopwatch"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="13" r="8"></circle>
                      <path d="M12 9v4l2 2" className="countdown-electoral__stopwatch-hand"></path>
                      <path d="M5 3L2 6"></path>
                      <path d="M22 6l-3-3"></path>
                      <path d="M10 2h4"></path>
                    </svg>
                  </div>
                  <div className="countdown-electoral__card-info">
                    <span className="countdown-electoral__number">{formatNumber(timeLeft.minutes)}</span>
                    <span className="countdown-electoral__label">MINUTOS</span>
                  </div>
                </div>
                <div className="countdown-electoral__card-line" aria-hidden="true"></div>
              </div>

              {/* CARD 4: SEGUNDOS */}
              <div className="countdown-electoral__card">
                <div className="countdown-electoral__card-inner">
                  <div className="countdown-electoral__icon-circle" aria-hidden="true">
                    <svg
                      className="countdown-electoral__svg-seconds"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 22h14" />
                      <path d="M5 2h14" />
                      <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" />
                      <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
                    </svg>
                  </div>
                  <div className="countdown-electoral__card-info">
                    <span className="countdown-electoral__number">{formatNumber(timeLeft.seconds)}</span>
                    <span className="countdown-electoral__label">SEGUNDOS</span>
                  </div>
                </div>
                <div className="countdown-electoral__card-line" aria-hidden="true"></div>
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default CountdownElectoral;
